import { useEffect, useRef } from "react";

import { EntityId } from "@reduxjs/toolkit";

import { Stack } from "@mui/material";

import { useAppSelector, useAppDispatch } from "app/hooks";
import { liveStreamingActions, liveStreamingThunks } from "./store/slice";
import { liveStreamingSelectors } from "./store/selectors";
import { selectPagedDevicesBySiteId } from "features/devices/store/selectors";
import {
    selectLiveStreamingConfiguration,
    selectSiteEventsConfiguration,
} from "features/config/store/selectors";
import { selectInWindowMode } from "features/core/store/selectors";

import { useKeyDownListener } from "hooks/useKeyDownListener";

import { FeatureFlagGuard } from "features/appFeatures/FeatureFlagGuard";
import { AppFeature } from "features/appFeatures/types/AppFeature";

import { scrollToElement } from "utils/scrollToElement";

import { DefaultPageLayout } from "features/core/DefaultPageLayout";
import { LiveStreamingPlayerGrid } from "./LiveStreamingPlayerGrid";
import { Sidebar } from "./Sidebar";
import { PermalinkLiveStream } from "./PermalinkLiveStream";
import { IncidentViewer } from "features/incidents/IncidentViewer";
import { LiveStreamingDebugControls } from "./LiveStreamingDebugControls";

export interface LiveStreamingProps {
    drawerWidth: number;
}

export function LiveStreaming(props: LiveStreamingProps) {
    const dispatch = useAppDispatch();

    // configuration
    const liveStreamingConfiguration = useAppSelector(
        selectLiveStreamingConfiguration,
    );
    const siteEventsConfig = useAppSelector(selectSiteEventsConfiguration);

    // get data from store
    const activeSiteId = useAppSelector(
        liveStreamingSelectors.selectActiveSiteId,
    );
    const siteDeviceSelections = useAppSelector(
        liveStreamingSelectors.selectSiteDeviceSelections,
    );

    const activeSiteDeviceSelection = useAppSelector(
        liveStreamingSelectors.selectActiveSiteDeviceSelection,
    );
    const selectedDevicesForActiveSite = useAppSelector(
        liveStreamingSelectors.selectSelectedDeviceIdsForActiveSite,
    );

    const session = useAppSelector(liveStreamingSelectors.selectSession);

    // check if there is a active site and if not select first available
    useEffect(() => {
        if (!activeSiteId) {
            dispatch(liveStreamingThunks.setFirstAvailableSiteAsActiveAsync());
        }
    }, [activeSiteId, dispatch]);

    // fetch devices and default selection when unknown site is set as active site
    useEffect(() => {
        if (
            activeSiteId &&
            liveStreamingConfiguration?.paginationPageSize &&
            !selectedDevicesForActiveSite
        ) {
            dispatch(
                liveStreamingThunks.initializeSiteAsync({
                    siteId: activeSiteId,
                    pageSize: liveStreamingConfiguration?.paginationPageSize,
                }),
            );
        }
    }, [
        activeSiteId,
        dispatch,
        selectedDevicesForActiveSite,
        liveStreamingConfiguration?.paginationPageSize,
    ]);

    // fetch the default device selection for this site
    const defaultDeviceSelectionForSite = useAppSelector((state) => {
        if (!activeSiteId || !liveStreamingConfiguration?.paginationPageSize)
            return undefined;

        const pageNumber = 1;

        const defaultSiteDeviceSelection = selectPagedDevicesBySiteId(
            state,
            activeSiteId,
            pageNumber,
            liveStreamingConfiguration?.paginationPageSize,
        );

        return defaultSiteDeviceSelection;
    });

    // fullscreen functionality
    const inWindowMode = useAppSelector(selectInWindowMode);

    useKeyDownListener(
        (e: KeyboardEvent) => {
            switch (e.key) {
                case "ArrowLeft":
                    dispatch(
                        liveStreamingActions.setPreviousFullscreenDeviceId(),
                    );
                    break;
                case "ArrowRight":
                    dispatch(liveStreamingActions.setNextFullscreenDeviceId());
                    break;
            }
        },
        ["ArrowLeft", "ArrowRight"],
    );

    // scroll functionality
    const listRef = useRef<Record<string, HTMLElement>>({});

    useEffect(() => {
        if (activeSiteId) {
            // delay is needed for correct scrolling
            setTimeout(() => {
                scrollToElement(listRef.current[activeSiteId]);
            }, 200);
        }
    }, [listRef, activeSiteId]);

    return (
        <>
            <DefaultPageLayout
                drawerWidth={props.drawerWidth}
                hideHeaderBarInFullscreen={true}
                sidebar={
                    <Sidebar
                        onSiteSelected={(siteId: EntityId) =>
                            dispatch(
                                liveStreamingActions.setSiteAsActive({
                                    siteId,
                                }),
                            )
                        }
                        onSiteClosed={(siteId: EntityId) => {
                            dispatch(
                                liveStreamingThunks.closeSiteAsync(siteId),
                            );
                        }}
                        onToggleDevice={(
                            siteId: EntityId,
                            deviceId: EntityId,
                            selected: boolean,
                        ) =>
                            dispatch(
                                liveStreamingActions.setDeviceSelectionState({
                                    siteId,
                                    deviceId,
                                    selected,
                                }),
                            )
                        }
                        onSetSelectionStateOfAllDevicesInSite={(
                            siteId: EntityId,
                            selected: boolean,
                        ) => {
                            dispatch(
                                liveStreamingActions.setSelectedDevicesForSite({
                                    siteId,
                                    deviceIds: selected
                                        ? (
                                              defaultDeviceSelectionForSite ??
                                              []
                                          ).map((d) => d.id)
                                        : [],
                                    deviceSelectionIsDoneByPaging: false,
                                    pageNumber: 1,
                                }),
                            );
                        }}
                        activeSiteId={activeSiteId}
                        selectedDeviceIds={selectedDevicesForActiveSite}
                        thumbnailRefreshIntervalInMs={
                            liveStreamingConfiguration?.thumbnailRefreshIntervalInMs
                        }
                    />
                }
                main={
                    <>
                        <FeatureFlagGuard
                            accessibleWithFeatureFlags={[
                                AppFeature.AdminDebugTools,
                            ]}
                        >
                            <LiveStreamingDebugControls />
                        </FeatureFlagGuard>

                        <Stack spacing={inWindowMode ? 3 : 0}>
                            <FeatureFlagGuard
                                accessibleWithFeatureFlags={[
                                    AppFeature.LiveStreamingIncidentFootage,
                                ]}
                            >
                                {session.origin === "landing-page" &&
                                    session.siteId &&
                                    session.startTime && (
                                        <IncidentViewer
                                            siteId={session.siteId}
                                            incidentTime={session.startTime}
                                            incidentsPollingIntervalInMs={
                                                liveStreamingConfiguration?.incidentsPollingIntervalInMs ??
                                                0
                                            }
                                        />
                                    )}
                            </FeatureFlagGuard>
                            {activeSiteDeviceSelection && (
                                <LiveStreamingPlayerGrid
                                    ref={(childRef: HTMLElement) => {
                                        if (listRef.current)
                                            listRef.current[
                                                activeSiteDeviceSelection.siteId
                                            ] = childRef;
                                    }}
                                    key={activeSiteDeviceSelection.siteId}
                                    siteId={activeSiteDeviceSelection.siteId}
                                    selectedDevices={
                                        activeSiteDeviceSelection.selectedDevices
                                    }
                                    refreshIntervalInMsUnstaffed={
                                        siteEventsConfig?.refreshIntervalInMsUnstaffed ??
                                        0
                                    }
                                    schedulesPollingIntervalInMs={
                                        liveStreamingConfiguration?.schedulesPollingIntervalInMs ??
                                        0
                                    }
                                    minutesBackInTimeForBadConnection={5}
                                    onCloseSite={(siteId: EntityId) =>
                                        dispatch(
                                            liveStreamingThunks.closeSiteAsync(
                                                siteId,
                                            ),
                                        )
                                    }
                                />
                            )}
                        </Stack>
                    </>
                }
            />

            <PermalinkLiveStream siteDeviceSelections={siteDeviceSelections} />
        </>
    );
}
